import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import Datetime from '@iftek/react-datetime'
import Autocomplete from 'react-autocomplete';
import moment from 'moment';
import _ from 'lodash';
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';
import { endOfDay } from "date-fns";

import SearchBar from '../../SearchBar'
import CustomButton from '../../CustomButton';
import DateDropdown from '../../dump/menuComponents/DateDropdown';
import AddSubscriptionModal from './Modals/AddSubscriptionModal';
import ModalMovementLog from "./Modals/ModalMovementLog";
import StopReportBSTable from './StopReportBSTable';
import IFTAReportTable from './IFTAReportTable';

import { getDateRangeFromTabKey } from './ReportsHelpers';
import { tableExportOptions, paginationExportOptions, expandTableOptions } from '../../helpers/table';
import momenttz from '../../helpers/momenttz'
import { getReportFirmware } from '../../../action/AccountActions';
import {
    getDevicesFeatureUsage,
    getFleetAdminEventsSummary,
} from '../../../action/FleetAdminActions';
import {
    getCompanyDevicesList,
    getDeviceMetadata,
    getDeviceMetadataHistory,
    getDevicesList
} from '../../../action/DeviceActions';
import { getCompanyById, getCompanyZones } from '../../../action/CompaniesActions';
import { getEventsOfCompany } from '../../../action/AlertActions';
import {
    getDataTrackingCompanies, getDataTrackingDevices,
    getDriverEventBreakdownReport, getDriverEventPerformanceReport,
    getEventBreakdownReport, getExitEntryReport,
    getIdlingBreakdownReport,
    getIdlingSummaryReport, getMovementsReport, getPairedGeotabDevices, getStopReport,
    setReportAddressList
} from '../../../action/ReportsActions';
import { exportIcon } from "../../helpers/iconHelpers";

const StandardReportTableBase = (props) => {
    const { goBack, selectedReport } = props    

    const navigate = useNavigate();
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'reportsPage.standardReportTable' });
    const tableRef = useRef();
    const user = useSelector(state => state.user.user);
    const company = useSelector(state => state.company.company);    
    const isProd = location.hostname === 'fleet.roscolive.com';
    const customDV7Access = isProd && [669, 1059, 1053, 3801, 2300].includes(company?.company_id);
    const adasEventTypes = ['headwaywarning', 'forwardcollisionwarning', 'pedestrianwarning', 'lanedeparture']
        .filter(eventType => !(customDV7Access && eventType === 'pedestrianwarning'))
        .join(',');
    const eventDetailEventTypes = [
        'fatalgsensor', 'highspeed', 'inputsensor', '%_clip', '%_alert', '%_event',
        'forwardcollisionwarning', 'lanedeparture', 'pedestrianwarning', 'headwaywarning',
        'drowsiness', 'distraction', 'phoneuse', 'yawning', 'seatbelt', 'smoking',
        'gsensor_wakeup', 'backup_warning', 'front_vehicle_start', 'trafficlightviolation_saw', 'trafficlightviolation_svw',
        'stopsignviolation_rsw', 'stopsignviolation_bsw', 'blind_spot_warning_standard', 'blind_spot_warning_urban',
        'low_speeding', 'moderate_speeding', 'high_speeding', 'severe_speeding',
    ].filter(eventType => !(customDV7Access && eventType === 'pedestrianwarning')).join(',');
    const zonesExitEntryReport = useSelector(state => state.reports.zonesExitEntryReport);
    
    const userRole = user.roles[0];
    const { settings = {} } = user;
    const userTZ = settings.time_zone || null;
    const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dispatch = useDispatch();
    let initTabKey = 3;
    switch (props.selectedReport.defaultDateRange) {
        case 'Today':
            initTabKey = 1;
            break;
        case 'Yesterday':
            initTabKey = 2;
            break;
        case 'Last Week':
            initTabKey = 4;
            break;
        case 'This Month':
            initTabKey = 5;
            break;
        case 'Last Month':
            initTabKey = 6;
            break;
    }
    const dateRange = getDateRangeFromTabKey(initTabKey);
    const CSV_HEADERS = [
        { label: i18n.t('reportsPage.standardReportData.Date'), key: "date" },
        { label: i18n.t('reportsPage.standardReportData.Jurisdiction'), key: "jurisdiction" },
        { label: i18n.t('reportsPage.standardReportData.Group'), key: "group" },
        { label: i18n.t('reportsPage.standardReportData.Vehicle'), key: "vehicle_name" },
        { label: i18n.t('reportsPage.standardReportData.Distance'), key: "distance" },
    ];
        
    const [reportSearch, setReportSearch] = useState('');
    const [reportData, setReportData] = useState([]);
    const [reportAdditionalData, setReportAdditionalData] = useState({});
    const [secondaryReportSearch, setSecondaryReportSearch] = useState('');
    const [secondaryReportData, setSecondaryReportData] = useState([]);
    const [showAddSubscription, setShowAddSubscription] = useState(false);
    const [showModalMovementLog, setShowModalMovementLog] = useState(false);
    const [start_date, setStart_date] = useState(dateRange[0]);
    const [end_date, setEnd_date] = useState(dateRange[1]);
    const [allCompanies, setAllCompanies] = useState(false);
    const [autoValue, setAutoValue] = useState('');
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState({});
    const [companyData, setCompanyData] = useState({});
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [allZones, setAllZones] = useState([]);
    const [selectedZoneId, setSelectedZoneId] = useState(null);
    const [movementLogData, setMovementLogData] = useState({});
    const [exportToCSVData, setExportToCSVData] = useState([]);

    const disabledReport = +selectedReport.id === 11 // 'Recording Status (Under Improvement)'

    useEffect(() => {
        if (!allCompanies) {
            setReportData([]);
            setReportAdditionalData({});
            setReportSearch('');
            setAutoValue('');
            getReportParameters(start_date, end_date, '', null);
        }
    }, [company.company_id]);

    useEffect(() => {
        if (selectedZoneId && selectedReport.name === 'Zone Exit/Entry Report') {
            getZoneData(selectedZoneId);
        }
    }, [start_date, end_date])

    const toggleLoader = useCallback((show) => {
        dispatch({ type: 'TOGGLE_LOADER', payload: show });
    }, [dispatch]);

    const getReportParameters = (start_date, end_date, autoValueArg, filters) => {
        switch (user.roles[0]) {
            case 'fleet_maintainer':
            case 'fleet_manager':
            case 'group_manager':
            case 'user':
            case 'storage_manager':
            case 'storage_user':
            case 'custom_user':
                getReportData(start_date, end_date, null, null, autoValueArg, filters);
                break;
            case 'system_admin':
            case 'customer_service':
            case 'sales_demo':
                if (company.name && company.partner) {
                    getReportData(start_date, end_date, company.name, company.partner, autoValueArg, filters);
                }
                break;
            case 'partner':
            case 'partner_view':
            case 'custom_partner':
                if (company.name) {
                    getReportData(start_date, end_date, company.name, null, autoValueArg, filters);
                }
                break;
            default:
                break;
        }
    }

    const getReportData = (start_date, end_date, company_name, partner_name, autoValueArg, filters) => {
        toggleLoader(true);
        
        const data = { start_date, end_date }

        switch (selectedReport.name) { // TODO: replace to selectedReport.id value
            case 'Asset Activity':
                getFleetAdminEventsSummary(userRole, company_name, partner_name)
                    .then((res) => {
                        setReportData(res.data.response)
                        toggleLoader(false)
                    })
                    .catch((err) => {
                        toastr.error(t('toastrUnableGetData'))
                        toggleLoader(false)
                    })
                break;
            case 'Feature Usage':
                if (!(['custom_user', 'custom_partner'].includes(userRole) && !_.find(user.permissions, { perm_category_id: 7, a_view: true }))) {
                    getDevicesFeatureUsage(data, userRole, company_name, partner_name)
                        .then(res => {
                            setReportData(res.data.response.report)
                            toggleLoader(false);
                        })
                        .catch(err => {
                            toastr.error(t('toastrUnableGetData'));
                            toggleLoader(false);
                        })
                } else {
                    toastr.error(t('toastrUnableGetData'));
                    toggleLoader(false);
                }
                break;
            case 'Asset Event Breakdown':
                getEventBreakdownReport({
                    company_id: company.company_id,
                    start_date: momenttz(moment(`${start_date}000000`, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')).utc().format('YYYYMMDDHHmmss'),
                    end_date: momenttz(moment(`${end_date}235959`, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')).utc().format('YYYYMMDDHHmmss'),
                })
                    .then(res => {
                        const vehicleEvents = res.data.response.report.map((item) => {
                            const { device_id, vehicle_name, vehicle_group, events } = item
                            return { device_id, vehicle_name, vehicle_group, ...events }
                        })
                        setReportData(vehicleEvents)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toastr.error(t('toastrUnableGetData'));
                        toggleLoader(false);
                    })
                break;
            case 'Group Event Breakdown':
                getEventBreakdownReport({
                    company_id: company.company_id,
                    start_date: momenttz(moment(`${start_date}000000`, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')).utc().format('YYYYMMDDHHmmss'),
                    end_date: momenttz(moment(`${end_date}235959`, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')).utc().format('YYYYMMDDHHmmss'),
                })
                    .then(res => {
                        const groupEvents = _(res.data.response.report)
                            .groupBy('vehicle_group')
                            .map((group, id) => {
                                const events = { vehicle_group: id }
                                group.forEach((event) => {
                                    // get list of keys
                                    Object.keys(event.events).forEach((key) => {
                                        events[key] = (events[key] ? events[key] : 0) + event.events[key]
                                    })
                                })

                                return events
                            })
                            .value()
                        setReportData(groupEvents)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toastr.error(t('toastrUnableGetData'));
                        toggleLoader(false);
                    })
                break;
            case 'Driver Event Breakdown':
                getDriverEventBreakdownReport({
                    company_id: company.company_id,
                    start_date: momenttz(moment(`${start_date}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDD'),
                    end_date: momenttz(moment(`${end_date}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDD'),
                })
                    .then(res => {
                        const eventBreakdown = res.data.response.report
                            .map((item) => {
                                let obj = item['event_counts'];
                                return { ...item, ...obj }
                            })
                        setReportData(eventBreakdown)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toastr.error(t('toastrUnableGetData'));
                        toggleLoader(false);
                    })
                break;
            case 'Driver Event Performance':
                getDriverEventPerformanceReport({
                    company_id: company.company_id,
                    start_date: momenttz(moment(`${start_date}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDD'),
                    end_date: momenttz(moment(`${end_date}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDD'),
                })
                    .then(res => {
                        setReportData(res.data.response.report)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toastr.error(t('toastrUnableGetData'));
                        toggleLoader(false);
                    })
                break;
            case 'Firmware':
                getReportFirmware(userRole, company_name, partner_name)
                    .then(res => {
                        setReportData(res.data.response.devices)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toastr.error(t('toastrUnableGetData'));
                        toggleLoader(false);
                    })
                break;
            case 'Recording Status (Under Improvement)':
                setReportData([]);
                setReportAdditionalData({});
                toggleLoader(false);
            case 'Channel Status':
                getDevicesList(company.company_id, `ignition,${selectedReport.name === 'Channel Status' ? 'channel_status' : 'sd_status'}`)
                    .then(res => {
                        let devices = selectedReport.name === 'Recording Status (Under Improvement)' ?
                            res.data.response.devices.sort((a, b) => a.sd_status === 'Needs Reformat' ? -1 : 1) :
                            res.data.response.devices.sort((a, b) => a.channel_status.length > b.channel_status.length ? -1 : 1);
                        setReportData(devices)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toastr.error(t('toastrUnableGetData'));
                        toggleLoader(false);
                    })
                break;
            case 'Data Usage':
                const requestArray = []
                requestArray.push(getCompanyById(userRole, company.company_id));
                requestArray.push(getDevicesList(company.company_id, 'inactive_devices,latest_hdderror,data_usage,ignition,sd_status,channel_status,driver,live_stream,no_sd_card,camera_health'));

                Promise.all(requestArray).then(
                    (res) => {
                        const companyData = res[0].data.response.company
                        let devices = res[1].data.response.devices.sort((a, b) => a.data_usage > b.data_usage ? -1 : 1)
                        setReportData(devices);
                        setCompanyData(companyData);
                        toggleLoader(false);
                    }
                )
                    .catch(err => {
                        toastr.error(t('toastrUnableGetData'));
                        toggleLoader(false);
                    })
                break;
            case 'DMS Events':
                getEventsOfCompany({
                    user_role: userRole,
                    company_name: company_name,
                    partner_name: partner_name,
                    start_ts: moment(start_date).utc().format('YYYYMMDDHHmmss'),
                    end_ts: moment(end_date).add(1, 'days').subtract(1, 'seconds').utc().format('YYYYMMDDHHmmss'),
                    alert_types: '',
                    event_types: 'phoneuse,drowsiness,yawning,distraction,seatbelt,smoking,camerablock',
                    device_ids: 'all'
                })
                    .then(res => {
                        const typeFormatter = {
                            'Drowsiness': 'Drowsiness/Head Down',
                            'Distraction': 'Distraction',
                            'Phone Use': 'Phone Use/Hand to Head',
                            'Yawning': 'Yawning',
                            'Seatbelt': 'Seatbelt',
                            'Smoking': 'Smoking',
                            'CameraBlock': 'Camera Blocked/Misaligned'
                        }
                        const reportData = [];
                        res.data.response.forEach((device) => {
                            device.events.forEach((event) => {
                                reportData.push({
                                    device_id: device.device_id,
                                    vehicle_name: device.vehicle_name,
                                    timestamp: event.timestamp,
                                    type: i18n.t(`constants.eventNames.${(typeFormatter[event.event_type] || event.event_type).replace(/\s/g, "_")}`, (typeFormatter[event.event_type] || event.event_type)),
                                    event_id: event.id,
                                })
                            })
                        })
                        setReportData(reportData)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toggleLoader(false);
                        toastr.error(t('toastrUnableGetData'))
                    });
                break;
            case 'ADAS Events':
                getEventsOfCompany({
                    user_role: userRole,
                    company_name: company_name,
                    partner_name: partner_name,
                    start_ts: moment(start_date).utc().format('YYYYMMDDHHmmss'),
                    end_ts: moment(end_date).add(1, 'days').subtract(1, 'seconds').utc().format('YYYYMMDDHHmmss'),
                    alert_types: '',
                    event_types: adasEventTypes,
                    device_ids: 'all'
                })
                    .then(res => {
                        const typeFormatter = {
                            'headwaywarning': 'Headway Warning',
                            'forwardcollisionwarning': 'Forward Collision',
                            'pedestrianwarning': 'Pedestrian Warning',
                            'lanedeparture': 'Lane Departure',
                            'followingdistancewarning': 'Tailgating',
                        }
                        const reportData = [];
                        res.data.response.forEach((device) => {
                            device.events.forEach((event) => {
                                reportData.push({
                                    device_id: device.device_id,
                                    vehicle_name: device.vehicle_name,
                                    timestamp: event.timestamp,
                                    type: i18n.t(`constants.eventNames.${(typeFormatter[event.event_type] || event.event_type).replace(/\s/g, "_")}`, (typeFormatter[event.event_type] || event.event_type)),
                                    event_id: event.id,
                                })
                            })
                        })
                        setReportData(reportData);
                        toggleLoader(false);
                    })
                    .catch(err => {
                        setReportData([]);
                        setReportAdditionalData({});
                        toggleLoader(false);
                        console.log(err)
                        toastr.error(t('toastrUnableGetData'))
                    });
                break;
            case 'Event Detail':
                getEventsOfCompany({
                    user_role: userRole,
                    company_name: company_name,
                    partner_name: partner_name,
                    start_ts: moment(start_date).utc().format('YYYYMMDDHHmmss'),
                    end_ts: moment(end_date).add(1, 'days').subtract(1, 'seconds').utc().format('YYYYMMDDHHmmss'),
                    alert_types: '',
                    event_types: eventDetailEventTypes,
                    device_ids: 'all'
                })
                    .then(res => {
                        const typeFormatter = {
                            'Drowsiness': 'Drowsiness/Head Down',
                            'Distraction': 'Distraction',
                            'Phone Use': 'Phone Use/Hand to Head',
                            'Yawning': 'Yawning',
                            'Seatbelt': 'Seatbelt',
                            'Smoking': 'Smoking',
                        }
                        const reportData = [];
                        res.data.response.forEach((device) => {
                            device.events.forEach((event) => {
                                reportData.push({
                                    device_id: device.device_id,
                                    vehicle_name: device.vehicle_name,
                                    timestamp: event.timestamp,
                                    type: i18n.t(`constants.eventNames.${(typeFormatter[event.event_type] || event.event_type).replace(/\s/g, "_")}`, (typeFormatter[event.event_type] || event.event_type)),
                                    event_id: event.id,
                                })
                            })
                        })
                        setReportData(reportData)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toggleLoader(false);
                        toastr.error(t('toastrUnableGetData'))
                    });
                break;
            case 'Idling Summary':
                getIdlingSummaryReport(company.company_id, start_date, end_date)
                    .then(res => {
                        setReportData(res.data.response.report)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toggleLoader(false);
                        toastr.error(t('toastrUnableGetData'))
                    })
                break;
            case 'Idling Breakdown':
                getIdlingBreakdownReport(company.company_id, start_date, end_date)
                    .then(res => {
                        setReportData(res.data.response.report)
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toggleLoader(false);
                        toastr.error(t('toastrUnableGetData'))
                    })
                break;
            // case 'Speeding Breakdown':
            //     getSpeedingBreakdownReport(company.company_id, start_date, end_date)
            //         .then(res => {
            //             setReportData(res.data.response.report)
            //             toggleLoader(false);
            //         })
            //         .catch(err => {
            //             toggleLoader(false);
            //             toastr.error(t('toastrUnableGetData'))
            //         })
            //     break;
            // case 'Speeding Summary':
            //     getSpeedingSummaryReport(company.company_id, start_date, end_date)
            //         .then(res => {
            //             setReportData(res.data.response.report)
            //             toggleLoader(false);
            //         })
            //         .catch(err => {
            //             toggleLoader(false);
            //             toastr.error(t('toastrUnableGetData'))
            //         })
            //     break;
            case 'Location History':
                if (!devices.length) {
                    getCompanyDevicesList({ company_id: company.company_id })
                        .then(res => {
                            toggleLoader(false);
                            setDevices(res.data.response.devices);
                        })
                        .catch(e => {
                            console.error('getDevicesList', e)
                            toastr.error(t('toastrUnableRetrieveVehicles'))
                            setDevices(undefined);
                            setReportData([]);
                            setReportAdditionalData({});
                        })
                } else {
                    toggleLoader(false);
                }
                //If a vehicle is selected, refresh data
                if (selectedDevice.device_id && ((autoValueArg !== undefined && autoValueArg !== '') || (autoValueArg === undefined && autoValue !== ''))) {
                    getLocationHistory(selectedDevice.device_id, start_date);
                }
                break;
            case 'Data Tracking':
                getDataTrackingCompanies({
                    start_date: start_date,
                    end_date: end_date,
                })
                    .then(res => {
                        const { companies } = res.data.response
                        setReportData(companies)
                        if (selectedCompanies.length) {
                            getDevicesReportData(selectedCompanies, start_date, end_date)
                        }
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toastr.error(t('toastrUnableGetCompaniesData'));
                        toggleLoader(false);
                    })
                break;
            case 'Zone Exit/Entry Report':
                const params = {
                    date: moment().format('YYYYMMDDhhmmss'),
                    tzname: userTZ ? userTZ : localTZ,
                };
                getCompanyZones(company.company_id, params)
                    .then(res => {
                        const zones = res.data.response || [];
                        setAllZones(zones);
                        if (zonesExitEntryReport) {
                            const { zoneId } = zonesExitEntryReport;
                            setSelectedZoneId(zoneId);
                            getZoneData(zoneId);
                            dispatch({ type: 'RESET_ZONES_REPORT' });
                        } else if (zones[0] && !selectedZoneId) {
                            setSelectedZoneId(zones[0].id);
                            getZoneData(zones[0].id);
                        }
                        toggleLoader(false);
                    })
                    .catch((err) => {
                        toastr.error(t('toastrUnableGetData'));
                        toggleLoader(false);
                    });
                break;
            case 'Paired GO Device Report':
                getPairedGeotabDevices({ company_id: company.company_id })
                    .then(res => {
                        setReportData(res.data.response.devices);
                        toggleLoader(false);
                    })
                    .catch(err => {
                        toastr.error(t('toastrUnableGetCompaniesData'));
                        toggleLoader(false);
                    })
                break;
            case 'Stop Report':
                getStopReportData();
                break;
        }
    }

    const getDevicesReportData = (selectedCompaniesArg, startDateArg, endDateArg) => {
        const selectedCompaniesParam = selectedCompaniesArg || selectedCompanies
        const startDateParam = startDateArg || start_date
        const endDateParam = endDateArg || end_date

        if (!selectedCompaniesParam.length) {
            setSecondaryReportData([])
            return
        }

        toggleLoader(true)
        getDataTrackingDevices({
            company_ids: selectedCompaniesParam,
            start_date: startDateParam,
            end_date: endDateParam,
        })
            .then(res => {
                const { devices } = res.data.response
                setSecondaryReportData(devices)
                toggleLoader(false);
            })
            .catch(err => {
                toastr.error(t('toastrUnableGetDevicesData'));
                toggleLoader(false);
            })
    }

    const changeDate = (selected_start_date, selected_end_date) => {
        const new_start_date = selected_start_date ? moment(selected_start_date).format('YYYYMMDD') : start_date;
        const new_end_date = selected_end_date ? moment(selected_end_date).format('YYYYMMDD') : end_date;
        setStart_date(new_start_date);
        setEnd_date(new_end_date);
        getReportParameters(new_start_date, new_end_date, null, null);
    }

    const getLocationHistory = (device_id, start_date) => {
        //normalize timestamp into user's tz setting
        const start_ts = momenttz(moment(`${start_date}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDDHHmmss')
        const end_ts = moment(start_ts, 'YYYYMMDDHHmmss').add(24, 'h').format('YYYYMMDDHHmmss')
        toggleLoader(true);

        switch (user.roles[0]) {
            case 'system_admin':
            case 'customer_service':
            case 'partners':
                getDeviceMetadataHistory({
                    start_ts: start_ts,
                    end_ts: end_ts,
                    user_role: user.roles[0],
                    device_id
                })
                    .then(res => {
                        parseLocationHistory(res.data.response.metadata)
                    })
                    .catch(err => {
                        console.error('getLocationHistory', err)
                        toastr.error(t('toastrGetDeviceLocationHistory'))
                        toggleLoader(false);
                    })
                break
            default:
                getDeviceMetadata({
                    start_ts: start_ts,
                    end_ts: end_ts
                })
                    .then(res => {
                        parseLocationHistory(res.data.response.metadata)
                    })
                    .catch(err => {
                        console.error('getLocationHistory', err)
                        toastr.error(t('toastrGetDeviceLocationHistory'))
                        toggleLoader(false);
                    })
                break
        }
    }

    const parseLocationHistory = (location_data) => {
        const report = [];
        const coords = [];
        //duration used for time interval between dates
        //at 2 min, max number of coord pairs shouldn't exceed 720
        const duration = moment.duration(2, 'minutes');
        let last_ts;

        for (let i = 0; i < location_data.length; i++) {
            if (report.length == 0 && location_data[i].type == "Gps") {
                last_ts = location_data[i].timestamp
                report.push({ time: location_data[i].timestamp })
                coords.push({ lat: location_data[i].latitude, lng: location_data[i].longitude })
            } else if (location_data[i].type == "Gps") {
                //if the current ts is more than a minute after last_ts, add to report.
                if (moment(location_data[i].timestamp).subtract(duration) >= moment(last_ts)) {
                    last_ts = location_data[i].timestamp
                    report.push({ time: location_data[i].timestamp })
                    coords.push({ lat: location_data[i].latitude, lng: location_data[i].longitude })
                } else {
                    continue
                }
            }
        }

        //handle no data returned for date
        // address/list API has max list length of 1000
        if (report.length > 0 && coords.length <= 1000) {
            getAddress(coords.filter(e => e.lat !== null && e.lng !== null), report)
        } else {
            toggleLoader(false);
            toastr.info(t('toastrNoHistory'))
            setReportData([]);
            setReportAdditionalData({});
        }
    }

    const getAddress = (coords, report) => {
        const coords_list = coords.map(data => JSON.stringify(data))
        toggleLoader(true);

        setReportAddressList({ coords_list: coords_list })
            .then(res => {
                const { response } = res.data;

                if (response.length) {
                    report.map((data, i) => {
                        if (!!response[i]) {
                            const house_number = response[i].house_number || '';
                            const road = response[i].road || '';
                            const city = !!response[i].city ? `${response[i].city},` : '';
                            const state = response[i].state || '';
                            const postcode = response[i].postcode || '';

                            house_number ?
                                data.address = `${house_number} ${road} - ${city} ${state} ${postcode}` :
                                data.address = `${road} - ${city} ${state} ${postcode}`
                        } else {
                            data.address = " - ";
                        }
                    })
                } else {
                    report.map(data => {
                        data.address = " - ";
                    })
                }

                console.debug('report', report)
                toggleLoader(false);
                setReportData(report);
            })
            .catch(err => {
                console.error(err)
                const error = err.response.data.response.error;
                if ((error !== "required keys are: ['lng']") && (error !== "required keys are: ['lat']")) toastr.error(t('toastrErr'))
                report.map(data => {
                    data.address = " - "
                })
                console.debug('report', report)
                toggleLoader(false);
                setReportData(report);
            })
    }

    const nameFormat = (name) => (i18n.t(`reportsPage.standardReportData.${name.replace(/\s/g, "_")}`));

    const dateFormat = (date, format = 'MM/DD/YYYY') => date ? moment(date).format(format) : '';

    const selectCompany = (row) => {
        let newSelectedCompanies = []

        if (selectedCompanies.includes(row.company_id)) {
            newSelectedCompanies = selectedCompanies.filter((company_id) => company_id !== row.company_id)
        }
        else {
            newSelectedCompanies = [...selectedCompanies, row.company_id]
        }

        setSelectedCompanies(newSelectedCompanies)
        getDevicesReportData(newSelectedCompanies)
    }

    const selectAllCompanies = (e) => {
        // e.preventDefault()
        // e.stopPropagation()
        const checked = e.target.type === 'checkbox' ? e.target.checked : e.target.value

        let newSelectedCompanies = []
        if (checked) {
            newSelectedCompanies = reportData.map(({ company_id }) => company_id)
        }

        setSelectedCompanies(newSelectedCompanies)
        getDevicesReportData(newSelectedCompanies)
    }

    const getMovementLogData = async (row, startDate = start_date, endDate = end_date, arrIds = []) => {
        toggleLoader(true);
        const start_ts = momenttz(moment(startDate).format('YYYY-MM-DD 00:00:00')).utc().format('YYYYMMDDHHmmss');
        const end_ts = momenttz(moment(endDate).format('YYYY-MM-DD 23:59:59')).utc().format('YYYYMMDDHHmmss');

        let req = [];
        row && arrIds.push(row.device_id);
        arrIds.map(id => { req.push(getMovementsReport({
            company_id: company.company_id,
            zoneId: selectedZoneId,
            device_id: id,
            start_ts,
            end_ts
        })) });

        await Promise.all(req)
            .then(res => {
                let arr = [];
                res.map((r, i) => {
                    if (row) {
                        let movement_log = _.orderBy(r.data.response, ['entry_time'], ['desc']);
                        setMovementLogData({
                            ...row,
                            data: movement_log,
                            reportDates: {
                                startDate,
                                endDate
                            }
                        });
                        setShowModalMovementLog(true);
                    } else {
                        let data = r.data.response;
                        const obj = reportData.find(r => r.device_id === arrIds[i]);
                        data = data.map((e, ind) => ({ ...obj, ...{ ...e, visit_seq: ind + 1 } }));
                        arr = [...arr, ...data];
                        if (i === res.length - 1) {
                            setExportToCSVData(arr);
                        }
                    }
                })
                toggleLoader(false);
            })
            .catch(err => {
                toastr.error(t('toastrUnableGetData'))
                toggleLoader(false)
            })
    }

    const setDataFormat = (cell, row, column) => {
        if (selectedReport.name === 'Data Tracking' && column.name === 'Company') {
            return column.dataFormat(cell, row, selectedCompanies, selectCompany, navigate)
        } else if (selectedReport.name === 'Data Tracking' && column.name === 'Override Overage Limits') {
            return (
                column.dataFormat(
                    cell,
                    row,
                    null,
                    (e) => {
                        toggleLoader(true)
                        getDataTrackingCompanies({
                            start_date,
                            end_date,
                        })
                            .then(res => {
                                const { companies } = res.data.response
                                setReportData(companies)
                                if (selectedCompanies.length) {
                                    getDevicesReportData()
                                }
                                toggleLoader(false)
                            })
                            .catch(err => {
                                toastr.error(t('toastrUnableGetCompaniesData'));
                                toggleLoader(false)
                            })
                    },
                    toggleLoader
                )
            )
        } else if (selectedReport.name === 'Zone Exit/Entry Report' && column.name === "View_Movement_Log") {
            return (<CustomButton
                variant="link"
                onClick={() => getMovementLogData(row)}
                style={{ color: '#000' }}
            >
                <span style={{ paddingRight: "10px" }}><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="#0071b6"><path fill="#0071b6" fillRule="evenodd" d="M12 4.57a.5.5 0 0 0-.024-.235l-.013-.063a1.5 1.5 0 0 0-.18-.434c-.092-.15-.222-.28-.482-.54L8.711.707c-.259-.26-.389-.39-.54-.483a1.5 1.5 0 0 0-.496-.193a.5.5 0 0 0-.235-.024C7.329.004 7.194.004 7.015.004h-2.21c-1.68 0-2.52 0-3.16.327a3.02 3.02 0 0 0-1.31 1.31C.008 2.283.008 3.12.008 4.8v6.4c0 1.68 0 2.52.327 3.16a3.02 3.02 0 0 0 1.31 1.31c.642.327 1.48.327 3.16.327h2.423c.401 0 .602-.523.347-.832a.45.45 0 0 0-.345-.168H4.8c-.857 0-1.44-.001-1.89-.038c-.438-.036-.663-.1-.819-.18a2 2 0 0 1-.874-.874c-.08-.156-.145-.38-.18-.819c-.036-.45-.037-1.03-.037-1.89v-6.4c0-.857 0-1.44.037-1.89c.036-.438.101-.663.18-.819c.192-.376.498-.682.874-.874c.156-.08.381-.145.82-.18C3.36.997 3.94.997 4.8.997H7v3.5a.5.5 0 0 0 .5.5H11v.547c0 .25.207.45.456.473c.285.025.543-.188.543-.474V4.99c0-.178 0-.313-.005-.425zM8 1.41L10.59 4H8z" clipRule="evenodd"></path><path fill="#0071b6" fillRule="evenodd" d="M11 15c.834 0 1.61-.255 2.25-.691l1.47 1.47a.749.749 0 1 0 1.06-1.06l-1.47-1.47c.436-.641.691-1.41.691-2.25c0-2.21-1.79-4-4-4s-4 1.79-4 4s1.79 4 4 4zm0-1c1.66 0 3-1.34 3-3s-1.34-3-3-3s-3 1.34-3 3s1.34 3 3 3" clipRule="evenodd"></path></svg></span>
                {i18n.t('constants.tablesFuncHelpers.viewLog')}
            </CustomButton>)
        } else if (['DMS Events', 'Event Detail'].includes(selectedReport.name) && column.name === 'View Event') {
            return column.dataFormat(cell, row, navigate)
        }

        return column.dataFormat(cell, row, companyData, () => getReportData(start_date, end_date, null, null, null, null))
    }

    const setSecondaryDataFormat = (cell, row, column) => {
        if (selectedReport.name === 'Data Tracking' && column.name === 'Enable/Disable Device') {
            return (
                column.dataFormat(
                    +cell,
                    row,
                    null,
                    (e) => {
                        const newSecondaryReportData = secondaryReportData.map((item) => {
                            if (item.device_id === row.device_id) {
                                item.active = !item.active
                            }
                            return item
                        })
                        setSecondaryReportData(newSecondaryReportData)
                    }
                )
            )
        } else if (selectedReport.name === 'Data Tracking' && column.name === 'Asset') {
            return column.dataFormat(cell, row, navigate)
        }

        return column.dataFormat(cell, row, companyData, () => getReportData(start_date, end_date))
    }

    const setColumnName = (column) => {
        const res = []

        if (selectedReport.name === 'Data Tracking' && column.name === 'Company') {
            res.push(
                <input
                    key={'data-tracking-company-column'}
                    type="checkbox"
                    className="company-checkbox column-header"
                    checked={selectedCompanies.length === reportData.length}
                    onChange={selectAllCompanies}
                />
            )
        }

        res.push(nameFormat(column.name))
        return res
    }

    const getZoneData = (id) => {
        toggleLoader(true);
        const start_ts = momenttz(moment(start_date).format('YYYY-MM-DD 00:00:00')).utc().format('YYYYMMDDHHmmss');
        const end_ts = momenttz(moment(end_date).format('YYYY-MM-DD 23:59:59')).utc().format('YYYYMMDDHHmmss');
        getExitEntryReport({ id, company_id: company.company_id, start_ts, end_ts })
            .then(res => {
                const zone = res.data.response || [];
                setReportData(zone);
                toggleLoader(false);
            })
            .catch(err => {
                toastr.error(t('toastrUnableGetData'));
                toggleLoader(false);
            })
    }

    const getStopReportData = () => {
        toggleLoader(true);

        const start_ts = momenttz(moment(start_date).format('YYYY-MM-DD 00:00:00')).utc().format('YYYYMMDDHHmmss');
        const end_ts = momenttz(moment(end_date).format('YYYY-MM-DD 23:59:59')).utc().format('YYYYMMDDHHmmss');

        getStopReport({
            company_id: company.company_id,
            start_ts: start_ts,
            end_ts: end_ts,
        })
            .then(res => {
                const stopsResponse = res?.data?.response || [];

                const groupedStops = _(stopsResponse)
                    .groupBy((stop) => {
                        const stopDate = moment(stop.created_at || stop.start_ts).format('YYYY-MM-DD');
                        return `${stop.device_id || stop.vehicle_name}|${stop.driver_name || ''}|${stopDate}`;
                    })
                    .value();

                const formattedData = Object.values(groupedStops).map((stopsGroup) => {
                    const sortedStops = _.orderBy(stopsGroup, ['start_ts'], ['asc']);
                    const firstStop = sortedStops[0] || {};
                    const totalStopTime = sortedStops.reduce((sum, stop) => sum + (stop.duration || 0), 0);
                    const longStops = sortedStops.filter(stop => (stop.duration || 0) > 1800).length;

                    return {
                        id: firstStop.id,
                        created_at: firstStop.created_at || firstStop.start_ts,
                        date: firstStop.created_at || firstStop.start_ts || moment(start_date).format('YYYY-MM-DD HH:mm:ss'),
                        device_id: firstStop.device_id || firstStop.vehicle_name,
                        vehicle_name: firstStop.vehicle_name || firstStop.device_id,
                        driver_name: firstStop.driver_name || '',
                        stops: sortedStops.length,
                        total_stop_time: totalStopTime,
                        long_stops: longStops,
                        data: sortedStops,
                    };
                });

                setReportData(formattedData);
                toggleLoader(false);
            })
            .catch(err => {
                console.error('getStopReportData error:', err);
                toastr.error(t('toastrUnableGetData'));
                setReportData([]);
                toggleLoader(false);
            })
    }

    const { dateEditable, defaultDateRange } = selectedReport;
    const searchTerm = selectedReport.searchTerm || ['vehicle_name'];
    const displayItemName = (item) => {
        return item.vehicle_name || item.device_id
    }
    const getItemValue = (item) => {
        return item.vehicle_name || item.device_id;
    }
    const matchStateToTerm = (item, value) => {
        if (item.vehicle_name) {
            return item.vehicle_name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
        }

        return item.device_id.toString().toLowerCase().indexOf(value.toLowerCase()) !== -1;
    }

    let filteredReportData = []
    if (searchTerm === ['driver_name']) {
        if (reportSearch) {
            filteredReportData = reportData && reportData.filter(driver => {
                let name = driver.first_name + ' ' + driver.last_name;
                if (name.toLowerCase().includes(reportSearch.toLowerCase())) {
                    return driver;
                }
            })
        }
        else {
            filteredReportData = reportData;
        }
    }
    else if (searchTerm === ['address']) {
        if (reportSearch) {
            filteredReportData = reportData && reportData.filter(address => {
                if (address.toLowerCase().includes(reportSearch.toLowerCase())) {
                    return address;
                }
            })
        } else {
            filteredReportData = reportData
        }
    }
    else {
        filteredReportData = reportData && (reportSearch ?
            reportData.filter(vehicle => {
                let filteredVehicle = false;
                searchTerm.forEach(term => {
                    if (vehicle[term] && vehicle[term].toString().toLowerCase().includes(reportSearch.toLowerCase())) {
                        filteredVehicle = vehicle;
                    }
                })
                return filteredVehicle
            }) :
            reportData
        );
    }

    const filteredSecondaryReportData = secondaryReportData && (
        secondaryReportSearch
            ? secondaryReportData.filter(vehicle => {
                let filteredVehicle = false;
                // searchTerm.forEach(term => {
                ['vehicle_name', 'imei'].forEach(term => {
                    if (vehicle[term] && vehicle[term].toString().toLowerCase().includes(secondaryReportSearch.toLowerCase())) {
                        filteredVehicle = vehicle;
                    }
                })
                return filteredVehicle
            })
            : secondaryReportData
    )

    switch (selectedReport.name) {
        case 'Driver Event Breakdown':
        case 'Driver Event Performance':
        case 'Zone Exit/Entry Report':
            if (reportSearch) filteredReportData = reportData.filter(item => Object.values(item).toString().toLowerCase().includes(reportSearch.toLowerCase()));
    }

    const formatCSVFileName = () => {
        const reportName = selectedReport.name;
        const name = reportName.toLowerCase().replaceAll(' ', '_');
        const timeframe = moment(start_date).format('M-D-YY') + (start_date === end_date ? '' : '--' + moment(end_date).format('M-D-YY'))
        return name + t('CSVFileName') + timeframe + '.csv'
    };

    const formatCSVSecondaryFileName = () => {
        const timeframe = moment(start_date).format('M-D-YY') + (start_date === end_date ? '' : '--' + moment(end_date).format('M-D-YY'))
        return t('CSVFileName') + timeframe + '.csv'
    };

    const handleExportToCSV = () => {
        if (tableRef.current) {
            tableRef.current.handleExportCSV();
        }
    };

    const onExportToCSVBtn = () => {
        const arrIds = filteredReportData.length ? filteredReportData.map(item => (item = item.device_id)) : [];
        if (arrIds.length) {
            getMovementLogData(null, start_date, end_date, arrIds);
        } else {
            exportToCSVData.length && setExportToCSVData([]);
        }
        setTimeout(() => handleExportToCSV(), 1000);
    }

    const onExportToCSV = () => {
        if (exportToCSVData.length) {
            return exportToCSVData
        } else {
            return filteredReportData
        }
    }

    const goBackFunc = () => {
        if (window.history.state.prevPath !== '/zones') {
            goBack();
        } else {
            navigate(-1);
        }
    };

    const changeZone = (e) => {
        const id = +e.target.value;
        setSelectedZoneId(id);
        getZoneData(id);
    }

    const expandComponent = (row) => (
        <StopReportBSTable
            data={row.data || []}
        />
    );

    return (
        <div className="report">
            <div className='page-subheader'>
                <div className='subheader-section search-flex report-search'>
                    <SearchBar term={reportSearch} placeholder={t('placeholderSearch')} onSearch={(e) => setReportSearch(e.target.value)} />
                    {dateEditable ? (
                        selectedReport.name === 'Location History'
                            ?
                            <DateDropdown
                                start_date={start_date}
                                end_date={end_date}
                                onSelectDate={changeDate}
                                singleDate={true}
                                limitDates={['TODAY', 'YESTERDAY']}
                                maxDate={endOfDay(new Date())}
                            />
                            : (
                                selectedReport.name === 'Data Tracking'
                                    ?
                                    <div className="report-date-time">
                                        <Datetime
                                            inputProps={{ readOnly: true }}
                                            closeOnSelect
                                            timeFormat={false}
                                            viewMode={'months'}
                                            dateFormat={'MMMM YYYY'}
                                            defaultValue={moment(start_date).format('MMMM YYYY')}
                                            value={moment(start_date).format('MMMM YYYY')}
                                            isValidDate={function (current) {
                                                return current.isBefore(Datetime.moment().endOf('month'))
                                            }}
                                            onChange={(e) => { changeDate(null, moment(e).startOf('month'), moment(e).endOf('month')) }}
                                        />
                                    </div>
                                    :
                                    <>
                                        <DateDropdown
                                            start_date={start_date}
                                            end_date={end_date}
                                            onSelectDate={changeDate}
                                            maxDate={endOfDay(new Date())}
                                        />
                                    </>
                            )
                    ) : ''}

                    {!dateEditable && defaultDateRange &&
                        <CustomButton variant='primary' size='sm' disabled>{defaultDateRange}</CustomButton>
                    }
                </div>
                <div className='subheader-section'>
                    {!['Location History', 'Data Tracking'].includes(selectedReport.name) && ![21].includes(+selectedReport.id) && /* Paired GO Device Report */
                        <CustomButton variant='primary-outline' size='sm' disabled={disabledReport} onClick={() => setShowAddSubscription(true)}>{t('btnAutomateReport')}</CustomButton>
                    }
                    <CustomButton variant="secondary-outline" onClick={goBackFunc}>{t('btnBack')}</CustomButton>
                </div>
            </div>

            {disabledReport
                ? <main className='main-content-block'>
                    <div className='table-header'>
                        <span className='report-title'>{nameFormat(selectedReport.reportName || selectedReport.name)}</span>
                        <p>{t('titleReport')}</p>
                    </div>
                </main>
                : <main className='main-content-block'>
                    <div className={`table-header${(selectedReport.name === 'Zone Exit/Entry Report') ? ' flex' : ''}`}>
                        <span className='report-title'>{nameFormat(selectedReport.reportName || selectedReport.name)}</span>
                        {selectedReport.name === 'Zone Exit/Entry Report' ? (
                            <div className="info-container">
                                <div className="info-row">
                                    <p>{t('zoneName')}:</p>
                                    <select className="zone-select rosco-select" value={selectedZoneId || ''} onChange={changeZone}>
                                        {allZones.map((item) => <option key={item.id} value={item.id} className="menu-item">{item.zone_name}</option>)}
                                    </select>
                                </div>
                            </div>
                        ) : ''}
                        {selectedReport.name === 'Location History' &&
                            <div className='autocomplete-wrapper'>
                                <span>{t('locationHistoryWrapper')}</span>
                                <Autocomplete
                                    autoHighlight
                                    value={autoValue}
                                    inputProps={{ id: 'states-autocomplete' }}
                                    wrapperProps={{ className: 'search-autocomplete event-search-input' }}
                                    getItemValue={getItemValue}
                                    shouldItemRender={matchStateToTerm}
                                    menuStyle={{
                                        borderRadius: '3px',
                                        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        padding: '2px 0',
                                        fontSize: '90%',
                                        position: 'absolute',
                                        overflow: 'auto',
                                        maxHeight: '200px',
                                        right: '0',
                                        top: 'auto',
                                        zIndex: 100
                                    }}
                                    onChange={(e, value) => {
                                        setAutoValue(value);
                                        setSelectedDevice(value);
                                    }}
                                    onSelect={(value, item) => {
                                        setAutoValue(value);
                                        setSelectedDevice(item);
                                        getLocationHistory(item.device_id, start_date);
                                    }}
                                    renderMenu={function (items, value, style) {
                                        return <div className="items-list" style={{ ...style, ...this.menuStyle }}
                                            children={items} />
                                    }
                                    }
                                    renderItem={(item, isHighlighted) => (
                                        <div
                                            className={`item ${isHighlighted ? 'item-highlighted' : ''}`}
                                            key={item.device_id}
                                        >
                                            {displayItemName(item)}
                                        </div>
                                    )}
                                    renderInput={(props) => (
                                        <input {...props} placeholder={t('placeholderSearchVehicle')}
                                            className="form-control event-page-search" />
                                    )}
                                    items={devices}
                                />
                            </div>
                        }
                    </div>
                    {filteredReportData ? (
                        <>
                            <BootstrapTable
                                    ref={tableRef}
                                    data={filteredReportData}
                                    {...tableExportOptions}
                                    {...(selectedReport.name === 'Zone Exit/Entry Report' && {
                                        options: {
                                            ...tableExportOptions.options,
                                            exportCSVBtn: () => (
                                                <div className="export-btn-container">
                                                    <div className="btn-group">
                                                        <CustomButton
                                                            onClick={onExportToCSVBtn}
                                                            variant='primary-outline'
                                                            size='sm'
                                                            prefix='export-button'
                                                        >
                                                            {exportIcon}
                                                            <span className='export-label'>{t('titleExport')}</span>
                                                        </CustomButton>
                                                    </div>
                                                </div>),
                                            onExportToCSV: onExportToCSV
                                        }
                                    })}
                                    {...(selectedReport.name === 'Stop Report' && {
                                        ...expandTableOptions,
                                        expandComponent,
                                        options: {
                                            ...tableExportOptions.options,
                                            ...paginationExportOptions,
                                        }
                                    })}
                                    containerClass={selectedReport.containerClass}
                                    csvFileName={formatCSVFileName}
                                >
                                    {selectedReport.columns.map(column => {
                                        return (
                                            <TableHeaderColumn
                                                key={column.dataField}
                                                width={column.width}
                                                isKey={column.isKey || false}
                                                hidden={column.hidden || false}
                                                dataField={column.dataField}
                                                dataFormat={
                                                    column.dataFormat
                                                        ? (cell, row) => setDataFormat(cell, row, column)
                                                        : (cell, row) => cell
                                                }
                                                dataSort={column.dataSort}
                                                csvFormat={
                                                    column.csvFormat
                                                        ? (cell, row) => column.csvFormat(cell, row, companyData)
                                                        : (cell, row) => cell
                                                }
                                                csvHeader={column.csvHeader ? column.csvHeader : nameFormat(column.name)}
                                                tdStyle={column.tdStyle ? column.tdStyle : {}}
                                                export={column.export}
                                            >
                                                {!column.hidden && setColumnName(column)}
                                            </TableHeaderColumn>
                                        )
                                    })}
                            </BootstrapTable>
                        </>
                    ) : ''}
                    {/* Second table of the Data Tracking report */}
                    {selectedReport.secondaryReportName &&
                        <div className='secondary-table-container'>
                            <div className='table-header'>
                                <span className='report-title'>{nameFormat(selectedReport.secondaryReportName)}</span>
                                <p>{t('textSelectCompanies')}</p>

                                {secondaryReportData.length > 0 &&
                                    <div className="search-bar-container">
                                        <SearchBar term={secondaryReportSearch}
                                            placeholder={t('placeholderSearchDevices')}
                                            onSearch={(e) => setSecondaryReportSearch(e.target.value)} />
                                    </div>
                                }
                            </div>
                            {secondaryReportData.length
                                ?
                                <BootstrapTable
                                    data={filteredSecondaryReportData}
                                    {...tableExportOptions}
                                    containerClass={selectedReport.containerClass}
                                    csvFileName={formatCSVSecondaryFileName}
                                >
                                    {selectedReport.secondaryColumns.map(column =>
                                        <TableHeaderColumn
                                            key={column.dataField}
                                            width={column.width}
                                            isKey={column.isKey || false}
                                            hidden={column.hidden || false}
                                            dataField={column.dataField}
                                            dataFormat={
                                                column.dataFormat
                                                    ? (cell, row) => setSecondaryDataFormat(cell, row, column)
                                                    : (cell, row) => cell
                                            }
                                            dataSort={column.dataSort}
                                            csvFormat={column.csvFormat}
                                            csvHeader={nameFormat(column.name)}
                                        >
                                            {nameFormat(column.name)}
                                        </TableHeaderColumn>
                                    )}
                                </BootstrapTable>
                                :
                                // <div className="p-5 bg-light text-muted rounded text-center">
                                <div className="jumbotron-custom text-center">
                                    {t('textNoCompanySelected')}
                                </div>

                            }
                        </div>
                    }
                </main>
            }
            {showAddSubscription && <AddSubscriptionModal onHide={() => setShowAddSubscription(false)}
                report={{
                    type: 'standard',
                    report_id: selectedReport.id,
                }} />}
            {showModalMovementLog &&
                <ModalMovementLog
                    data={movementLogData}
                    getMovementLogData={getMovementLogData}
                    onHide={() => setShowModalMovementLog(false)}
                />
            }
        </div>
    )
}

const StandardReportTable = (props) => {
    if (+props.selectedReport.id === 19) {
        return <IFTAReportTable {...props} />;
    }

    return <StandardReportTableBase {...props} />;
};

export default StandardReportTable;
